import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { geographyApi } from "@/services/api-v2.service";

const STORAGE_KEY = "gpml.geographyFilter.v2";
const JOURNAL_SYNC_KEY = "gpml.journalGeographySync";

export type GeographyCombineMode = "any" | "all";

export type GeographyTerritory = {
  id: number;
  name: string;
  level: string;
  dataset: string | null;
};

export type GeographyBoundaryView = {
  features: GeoJSON.Feature[];
  bbox: [number, number, number, number] | null;
};

type Stored = {
  territories: GeographyTerritory[];
  combine: GeographyCombineMode;
  focusId?: number | null;
};

type GeographyFilterContextValue = {
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
  territories: GeographyTerritory[];
  combine: GeographyCombineMode;
  boundary: GeographyBoundaryView | null;
  /** Первая выбранная единица — для подписей, которым нужна одна строка. */
  territory: GeographyTerritory | null;
  toggleTerritory: (unit: GeographyTerritory) => void;
  setCombine: (mode: GeographyCombineMode) => void;
  clearTerritory: () => void;
  /** Журнал объектов на главной повторяет территориальный фильтр карты. По умолчанию выключено. */
  journalSync: boolean;
  setJournalSync: (enabled: boolean) => void;
  /** Глубина АТД для клика по карте: 1–4 или все найденные. */
  pointScope: GeographyPointScope;
  setPointScope: (scope: GeographyPointScope) => void;
  pointPick: boolean;
  setPointPick: (active: boolean) => void;
  /** Чертёж на карте: указание точки не включается. */
  pointPickBlocked: boolean;
  registerPointPickBlock: () => () => void;
  replaceTerritories: (units: GeographyTerritory[], mode: GeographyCombineMode, focusId?: number | null) => void;
  /** Самый нижний уровень последнего клика по карте. Ручной набор его не задаёт. */
  focusTerritoryId: number | null;
};

export type GeographyPointScope = 1 | 2 | 3 | 4 | "all";

const GeographyFilterContext = createContext<GeographyFilterContextValue | null>(null);

function readStored(): Stored {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { territories: [], combine: "any", focusId: null };
    const parsed = JSON.parse(raw) as Stored;
    return {
      territories: Array.isArray(parsed.territories) ? parsed.territories : [],
      combine: parsed.combine === "all" ? "all" : "any",
      focusId: typeof parsed.focusId === "number" ? parsed.focusId : null,
    };
  } catch {
    return { territories: [], combine: "any", focusId: null };
  }
}

export function territorySummary(items: GeographyTerritory[]): string {
  if (items.length === 0) return "Территория";
  if (items.length === 1) return items[0].name;
  return `${items[0].name} +${items.length - 1}`;
}

export function GeographyFilterProvider({ children }: { children: ReactNode }) {
  const stored = useMemo(() => readStored(), []);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [territories, setTerritories] = useState<GeographyTerritory[]>(stored.territories);
  const [combine, setCombineState] = useState<GeographyCombineMode>(stored.combine);
  const [focusTerritoryId, setFocusTerritoryId] = useState<number | null>(stored.focusId ?? null);
  const [boundary, setBoundary] = useState<GeographyBoundaryView | null>(null);
  const [pointScope, setPointScope] = useState<GeographyPointScope>("all");
  const [pointPick, setPointPick] = useState(false);
  const [pointPickBlocked, setPointPickBlocked] = useState(false);
  const pointPickBlocks = useRef(0);
  const [journalSync, setJournalSyncState] = useState(() => {
    try {
      return localStorage.getItem(JOURNAL_SYNC_KEY) === "1";
    } catch {
      return false;
    }
  });

  const persist = useCallback((next: GeographyTerritory[], mode: GeographyCombineMode, focusId: number | null) => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ territories: next, combine: mode, focusId } satisfies Stored),
    );
  }, []);

  const clearTerritory = useCallback(() => {
    setTerritories([]);
    setFocusTerritoryId(null);
    setBoundary(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const setCombine = useCallback((mode: GeographyCombineMode) => {
    setCombineState(mode);
    setTerritories((current) => {
      persist(current, mode, focusTerritoryId);
      return current;
    });
  }, [persist, focusTerritoryId]);

  const replaceTerritories = useCallback((
    units: GeographyTerritory[],
    mode: GeographyCombineMode,
    focusId?: number | null,
  ) => {
    const next = units.slice(0, 20);
    const focus = focusId != null && next.some((unit) => unit.id === focusId) ? focusId : null;
    setCombineState(mode);
    setFocusTerritoryId(focus);
    setTerritories(next);
    if (next.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    persist(next, mode, focus);
  }, [persist]);

  const registerPointPickBlock = useCallback(() => {
    pointPickBlocks.current += 1;
    setPointPickBlocked(true);
    return () => {
      pointPickBlocks.current = Math.max(0, pointPickBlocks.current - 1);
      setPointPickBlocked(pointPickBlocks.current > 0);
    };
  }, []);

  const toggleTerritory = useCallback((unit: GeographyTerritory) => {
    setTerritories((current) => {
      const exists = current.some((item) => item.id === unit.id);
      const next = exists ? current.filter((item) => item.id !== unit.id) : [...current, unit].slice(0, 20);
      const focus = focusTerritoryId != null && next.some((item) => item.id === focusTerritoryId)
        ? focusTerritoryId
        : null;
      setFocusTerritoryId(focus);
      persist(next, combine, focus);
      return next;
    });
  }, [combine, persist, focusTerritoryId]);

  const idsKey = territories.map((item) => item.id).join(",");

  useEffect(() => {
    if (!idsKey) {
      setBoundary(null);
      return;
    }
    let cancelled = false;
    geographyApi.catalog
      .boundaries(idsKey)
      .then((payload) => {
        if (cancelled) return;
        const bbox =
          payload.bbox && payload.bbox.length === 4
            ? (payload.bbox as [number, number, number, number])
            : null;
        setBoundary({
          features: (payload.features ?? []) as GeoJSON.Feature[],
          bbox,
        });
      })
      .catch(() => {
        if (!cancelled) setBoundary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  const setJournalSync = useCallback((enabled: boolean) => {
    setJournalSyncState(enabled);
    try {
      localStorage.setItem(JOURNAL_SYNC_KEY, enabled ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!pointPick) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setPointPick(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pointPick]);

  const territory = territories[0] ?? null;

  const value = useMemo(
    () => ({
      sheetOpen,
      setSheetOpen,
      territories,
      combine,
      boundary,
      territory,
      toggleTerritory,
      setCombine,
      clearTerritory,
      journalSync,
      setJournalSync,
      pointScope,
      setPointScope,
      pointPick,
      setPointPick,
      pointPickBlocked,
      registerPointPickBlock,
      replaceTerritories,
      focusTerritoryId,
    }),
    [
      sheetOpen,
      territories,
      combine,
      boundary,
      territory,
      toggleTerritory,
      setCombine,
      clearTerritory,
      journalSync,
      setJournalSync,
      pointScope,
      pointPick,
      pointPickBlocked,
      registerPointPickBlock,
      replaceTerritories,
      focusTerritoryId,
    ],
  );

  return (
    <GeographyFilterContext.Provider value={value}>{children}</GeographyFilterContext.Provider>
  );
}

export function useGeographyFilter(): GeographyFilterContextValue {
  const ctx = useContext(GeographyFilterContext);
  if (!ctx) {
    throw new Error("useGeographyFilter: нет GeographyFilterProvider");
  }
  return ctx;
}
